import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { gbp, monthLabel, ProfitPill } from './shared';

const BRAND_COLOURS = { probound: '#1d4ed8', proepoxy: '#7e22ce', prodecor: '#b45309' };

export function TurnoverChart({ months }) {
  const data = months.map((m) => ({ name: monthLabel(m.month), ProBound: m.billed_by_brand.probound, ProEpoxy: m.billed_by_brand.proepoxy, ProDecor: m.billed_by_brand.prodecor, Cash: m.cash }));
  return (
    <Card className="p-4" data-testid="turnover-chart">
      <h2 className="font-bold text-gray-900 mb-1">Monthly turnover</h2>
      <p className="text-[11px] text-gray-500 mb-3">Bars = fees billed for courses run that month, split by brand. Grey bar = cash actually received that month.</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} width={48} />
            <Tooltip formatter={(v) => gbp(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="ProBound" stackId="billed" fill={BRAND_COLOURS.probound} />
            <Bar dataKey="ProEpoxy" stackId="billed" fill={BRAND_COLOURS.proepoxy} />
            <Bar dataKey="ProDecor" stackId="billed" fill={BRAND_COLOURS.prodecor} radius={[3, 3, 0, 0]} />
            <Bar dataKey="Cash" fill="#9ca3af" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function CostInput({ month, field, value, onSaved }) {
  const [v, setV] = useState(value === 0 ? '' : String(value));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const num = v === '' ? 0 : Number(v);
    if (Number.isNaN(num) || num < 0) { toast.error('Enter a valid amount'); return; }
    if (num === Number(value)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-analytics/costs`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ month, [field]: num }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Save failed');
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-gray-400">£</span>
      <input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
        placeholder="0.00" className="w-24 h-7 px-1.5 text-right text-sm border border-gray-200 rounded bg-white focus:border-emerald-500 focus:outline-none tabular-nums"
        data-testid={`cost-${field}-${month}`} />
      {busy && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
    </span>
  );
}

export function MonthlyTable({ data, reload }) {
  const t = data.totals;
  const P = data.can_view_profit;
  return (
    <Card className="p-0 overflow-hidden" data-testid="monthly-table">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h2 className="font-bold text-gray-900">{P ? 'Turnover, costs & profit by month' : 'Turnover by month'}</h2>
        {P && <p className="text-[11px] text-gray-500 mt-0.5">Type your marketing spend (and any other costs) per month — saves when you leave the box. Profit = turnover ex VAT ({Math.round(data.vat_rate * 100)}%) − marketing − other − free C&amp;G certificates (£{data.cg_cost_gross_each.toFixed(2)} each).</p>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-gray-500 bg-white border-b">
            <tr>
              <th className="text-left px-4 py-2">Month</th>
              <th className="text-right px-3 py-2">Seats</th>
              <th className="text-right px-3 py-2">Billed</th>
              <th className="text-right px-3 py-2">Ex VAT</th>
              <th className="text-right px-3 py-2">Cash in</th>
              {P && <><th className="text-right px-3 py-2">Marketing</th>
              <th className="text-right px-3 py-2">Other</th>
              <th className="text-right px-3 py-2">C&amp;G cost</th>
              <th className="text-right px-3 py-2">Profit</th>
              <th className="text-right px-4 py-2">Profit (cash)</th></>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.months.map((m) => (
              <tr key={m.month} className="hover:bg-gray-50/60" data-testid={`month-row-${m.month}`}>
                <td className="px-4 py-2 font-semibold text-gray-900 whitespace-nowrap">{monthLabel(m.month)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700">{m.seats}{m.cancelled ? <span className="text-[10px] text-rose-600 ml-1">({m.cancelled} canc.)</span> : null}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold" data-testid={`month-billed-${m.month}`}>{gbp(m.billed)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">{gbp(m.billed_ex_vat)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-700" data-testid={`month-cash-${m.month}`}>{gbp(m.cash)}</td>
                {P && <><td className="px-3 py-2 text-right"><CostInput month={m.month} field="marketing_cost" value={m.marketing_cost} onSaved={reload} /></td>
                <td className="px-3 py-2 text-right"><CostInput month={m.month} field="other_cost" value={m.other_cost} onSaved={reload} /></td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">{m.free_cg ? gbp(m.cg_cost) : '—'}</td>
                <td className="px-3 py-2 text-right"><ProfitPill value={m.profit} testid={`month-profit-${m.month}`} /></td>
                <td className="px-4 py-2 text-right"><ProfitPill value={m.profit_cash} /></td></>}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t font-bold text-gray-900">
            <tr data-testid="month-totals-row">
              <td className="px-4 py-2">Total</td>
              <td className="px-3 py-2 text-right tabular-nums">{t.seats}</td>
              <td className="px-3 py-2 text-right tabular-nums" data-testid="total-billed">{gbp(t.billed)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-600">{gbp(t.billed_ex_vat)}</td>
              <td className="px-3 py-2 text-right tabular-nums" data-testid="total-cash">{gbp(t.cash)}</td>
              {P && <><td className="px-3 py-2 text-right tabular-nums" data-testid="total-marketing">{gbp(t.marketing_cost)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{gbp(t.other_cost)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{gbp(t.cg_cost)}</td>
              <td className="px-3 py-2 text-right"><ProfitPill value={t.profit} testid="total-profit" /></td>
              <td className="px-4 py-2 text-right"><ProfitPill value={t.profit_cash} /></td></>}
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}
